import type { i18n } from 'i18next';

import type { StackedNumbersList } from 'src/components/followUp/common/types';

export const HIDDEN_BARS = [
  'promoter',
  'detractor',
  'passive',
  'open',
  'closed',
  'overDue',
  'inProgress',
  'totalDetractors',
];

export const labelTranslationExists = (label: string, i18n: i18n) =>
  i18n.exists(`followUp:statistics.comparisonOfUnitsWidget.labels.${label}`);

export const getStackLabel = (
  label: string,
  t: (key: string) => string,
  i18n: i18n,
  addRelativeLabelsForQuestions?: boolean,
): string | string[] => {
  const key = `followUp:statistics.comparisonOfUnitsWidget.labels.${label}`;

  if (i18n.exists(key)) return t(key);

  return addRelativeLabelsForQuestions
    ? [`${label} (N)`, `${label} (%)`]
    : label;
};

export enum SENTIMENT_NAMES {
  'total' = 'valueTotal',
  'positive' = 'valuePositive',
  'negative' = 'valueNegative',
  'neutral' = 'valueNeutral',
}

type Stack = { stackLabel: string; value: number };
export interface StackTransformed extends Stack {
  [SENTIMENT_NAMES.total]?: number;
  [SENTIMENT_NAMES.positive]?: number;
  [SENTIMENT_NAMES.negative]?: number;
  [SENTIMENT_NAMES.neutral]?: number;
}

/**
 * Recognise a stack marked with a sentiment and add a new key value pair for
 * the sentiment. If there is already a stack with the same label then those
 * stacks will be combined.
 *
 * The reason this function exists is that the implemented stack-logic is very
 * restrictive and does not allow for multiple values. The workaround is
 * to send multiple stacks with prefixes from 'SENTIMENT_NAMES', e.g.:
 * {
 *   stackLabel: '[total] This is an example.',
 *   value: 10,
 * }
 * will resolve to:
 * {
 *   stackLabel: 'This is an example.',
 *   valueTotal: 10,
 *   value: 0,
 * }
 * Note: value will be set to 0 because it is a required prop, but it should
 * not be used after this because it is conflicting with the sentiment values.
 */
const collectSentimentStacks = (
  sentiment: keyof typeof SENTIMENT_NAMES,
  acc: StackTransformed[],
  { stackLabel, value }: Stack,
) => {
  const splitLabel = stackLabel.split(`[${sentiment}]`);
  if (splitLabel.length === 2 && splitLabel[0] === '') {
    const label = splitLabel[1].trim();
    const key = SENTIMENT_NAMES[sentiment];
    const prevStackIndex = acc.findIndex(
      ({ stackLabel: prevStackLabel }) => prevStackLabel === label,
    );
    if (prevStackIndex >= 0) {
      Object.assign(acc[prevStackIndex], { [key]: value });
      return acc;
    }
    return [...acc, { stackLabel: label, [key]: value, value: 0 }];
  }
  return false;
};

export const transformResults = (data: StackedNumbersList) => {
  const results = data.results.map(({ label, stacks }) => ({
    label,
    stacks: stacks.reduce((acc: StackTransformed[], stack) => {
      let sentiment: keyof typeof SENTIMENT_NAMES;
      for (sentiment in SENTIMENT_NAMES) {
        const result = collectSentimentStacks(sentiment, acc, stack);
        if (result) return result;
      }
      return [...acc, stack];
    }, []),
  }));

  return { ...data, results };
};
